import {
    keepPreviousData,
    useQuery,
    UseQueryResult,
} from '@tanstack/react-query'
import {AxiosError, AxiosResponse} from 'axios'
import { useSearchParams } from 'react-router-dom'
import { DEFAULT_POLLING_KEY, PollingState } from '../../CommonComponents/PollingButton.tsx'
import {useNet} from "@src/utils/net.ts";

const POLLING_INTERVAL = 5 * 1000

type QueryKey = [string] | [string, string | undefined];

const shouldRetry = (failureCount: number, error: unknown): boolean => {
    if (error instanceof AxiosError && error.response?.status === 403) return false
    return failureCount < 3
}

export const useEntityQuery = <T = unknown, D = T>(
    queryKey: [string, string | undefined],
    queryFn: (id: string, signal: AbortSignal) => Promise<AxiosResponse<T, AxiosError>>,
    staleTime = 3000,
    select?: (data: T) => D
): ReturnType<typeof useQuery<T, AxiosError, D>> => {
    const [params] = useSearchParams()
    const refetchInterval = params.get(DEFAULT_POLLING_KEY) === PollingState.on ? POLLING_INTERVAL : false
    const [, id] = queryKey
    return useQuery<T, AxiosError, D>({
        queryKey,
        enabled: !!id,
        refetchInterval,
        retry: shouldRetry,
        // @ts-ignore
        queryFn: async ({ signal }) => {
            if (!id) throw new Error('Expected id')
            const res = await queryFn(id, signal)
            return res.data
        },
        select,
        staleTime
    })
}

export const useListQuery = <T = unknown>(
    queryKey: QueryKey,
    queryFn: (search: string, signal: AbortSignal) => Promise<AxiosResponse<T, AxiosError>>,
    select?: (data: T) => T,
    disabled?: boolean,
): ReturnType<typeof useQuery<T, AxiosError>> => {
    const [params] = useSearchParams()

    const refetchInterval = params.get(DEFAULT_POLLING_KEY) === PollingState.on ? POLLING_INTERVAL : false
    const [entityKey, search] = queryKey

    const searchParams = new URLSearchParams(search)
    //@ts-ignore
    return useQuery<T, AxiosError>({
        enabled: !disabled,
        queryKey: [entityKey, searchParams.toString()],
        staleTime: 3000,
        retry: 1,
        placeholderData: keepPreviousData,
        refetchOnReconnect: "always",
        refetchOnWindowFocus: false,
        networkMode: "always",

        refetchInterval,
        select,
        //@ts-ignore
        queryFn: async ({ signal, queryKey }) => {
            const [_, searchParams] = queryKey as [string, string | undefined]
            const res = await queryFn(searchParams || '', signal)
            if (res?.data) {
                return res.data
            }
            return res
        },
    })
}

export function useQueryWithOffline<TData = unknown, TSelected = TData>(
    queryKey: QueryKey,
    onlineFn: (search: string, signal: AbortSignal) => Promise<TData>,
    offlineFn: (search: string, signal: AbortSignal) => Promise<TData> | TData,
    select?: (data: TData) => TSelected,
    disabled?: boolean
): UseQueryResult<TSelected, AxiosError> {
    const isOnline = useNet(s => s.online);
    const fn = isOnline ? onlineFn : offlineFn;
    return useListQuery<TData>(
        queryKey,
        //@ts-ignore
        fn,
        select,
        disabled ?? false
    ) as UseQueryResult<TSelected, AxiosError>;
}